import { LockOutlined } from "@ant-design/icons";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { Mail, Smartphone } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import India from "../../../assets/images/flags/india.png";
import USA from "../../../assets/images/flags/united-states-of-america.png";
import AuthIconTooltip from "../../../components/auth/AuthIconTooltip";
import ContactEntryDialogs from "../../../components/auth/ContactEntryDialogs";
import GoogleIconSignIn from "../../../components/auth/GoogleIconSignIn";
import {
  useForgotPasswordMutation,
  useLoginMutation,
  useLoginWithGoogleMutation,
  useSendPhoneOtpMutation,
  useGenerateEmailOtpMutation,
  useVerifyPhoneOtpMutation,
  useVerifyotpMutation,
  useVerifyEmailOtpMutation,
  useSendotpMutation
} from "../../../utils/apiSlice";
import { loginSuccess } from "../../../utils/authSlice";
import { runLoginGamification } from "../../../utils/gamificationService";
import { hydrateGamification } from "../../../utils/gamificationSlice";
import {
  isValidContactEmail,
  isValidContactPhoneDigits,
  type ContactEntryFieldErrors,
} from "../../../utils/contactEntryValidation";
import "./login.scss";
import ind from "../../../assets/in.png";
import us from "../../../assets/us.png";
import { Select } from "antd";
import { triggerCoins } from "../../canvasConfict";
const { Option } = Select;
export default function LoginPage() {
  const [selectedCountry, setSelectedCountry] = useState("IN");
  const countryDialCodeMap: Record<string, string> = {
    IN: "+91",
    US: "+1",
  };
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotEmailError, setForgotEmailError] = useState("");
  const [login] = useLoginMutation();
  const [isExistingUser, setIsExistingUser] = useState(true)
  const [loginWithGoogle] = useLoginWithGoogleMutation();
  const [loading, setLoading] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [hasOtpError, setHasOtpError] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [forgotPassword, setForgotPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotConfirmPasswordError, setForgotConfirmPasswordError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [generateEmailOtp] = useGenerateEmailOtpMutation();
  const [verifyOtp] = useVerifyotpMutation();
  const [sendPhoneOtp] = useSendPhoneOtpMutation();
  const [verifyPhoneOtp] = useVerifyPhoneOtpMutation();
  const [forgotPasswordApi] = useForgotPasswordMutation();
  const [contactType, setContactType] = useState<"email" | "phone">("email");
  const [sendOtp] = useSendotpMutation();

  const otpInputRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  const countries = [
    { code: "IN", name: "India", flag: ind },
    { code: "US", name: "USA", flag: us },
  ];

  const selected = countries.find((c) => c.code === selectedCountry);
  const [showPassword, setShowPassword] = useState(false);
  const [initialValues, setInitialValues] = useState<any>({
    username: "",
    password: "",
    remember: false,
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [contactEntryKind, setContactEntryKind] = useState<null | "email" | "phone">(null);
  const [draftFullName, setDraftFullName] = useState("");
  const [draftEmail, setDraftEmail] = useState("");
  const [draftPhone, setDraftPhone] = useState("");
  const [contactFieldErrors, setContactFieldErrors] = useState<ContactEntryFieldErrors>({});

  const clearContactFieldError = (field: "name" | "email" | "phone") => {
    setContactFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Phone OTP login (popup on top of login)
  const [showPhoneOtpModal, setShowPhoneOtpModal] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState(["", "", "", "", "", ""]);
  const [phoneHasOtpError, setPhoneHasOtpError] = useState(false);
  const [phoneCountdown, setPhoneCountdown] = useState(0);
  const phoneOtpRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  const [showEmailOtpModal, setShowEmailOtpModal] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState("");
  const [emailOtpDigits, setEmailOtpDigits] = useState(["", "", "", "", "", ""]);
  const [emailHasOtpError, setEmailHasOtpError] = useState(false);
  const [emailOtpCountdown, setEmailOtpCountdown] = useState(0);
  const emailOtpRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  const [verifyEmailOtpMutation] = useVerifyEmailOtpMutation();
  const isForbidden = (err: any) => {
    const s = err?.status ?? err?.originalStatus ?? err?.data?.statusCode;
    return s === 403 || s === "403";
  };

  const closeContactEntry = () => {
    setIsExistingUser(true)
    setContactEntryKind(null);
    setContactFieldErrors({});
  };

  const openEmailEntry = () => {
    setContactFieldErrors({});
    setDraftFullName("");
    setDraftEmail("");
    setContactEntryKind("email");
  };

  const openPhoneEntry = () => {
    setContactFieldErrors({});
    setDraftFullName("");
    setDraftPhone("");
    setContactEntryKind("phone");
  };

  const submitEmailEntry = () => {
    const fullName = String(draftFullName || "").trim();
    const e = String(draftEmail).trim();
    const next: ContactEntryFieldErrors = {};
    if (!fullName && !isExistingUser) next.name = "Enter your full name.";
    if (!isValidContactEmail(e)) next.email = "Enter a valid email address.";
    if (Object.keys(next).length) {
      setContactFieldErrors(next);
      return;
    }

    setContactFieldErrors({});
    closeContactEntry();
    setEmailToVerify(e);
    setEmailHasOtpError(false);
    setEmailOtpDigits(["", "", "", "", "", ""]);
    setEmailOtpCountdown(0);
    generateEmailOtp({ email: e })
      .unwrap()
      .then((resp: any) => {
        setShowEmailOtpModal(true);
        setEmailOtpCountdown(getOtpCountdownSeconds(resp, 30));
        toast.info(resp?.message || "OTP sent to your email");
        setIsExistingUser(true)
      })

      .catch((err: any) => {
        if (isForbidden(err)) return;
        let msg = "Failed to send OTP.";
        if (typeof err?.data === "string") msg = err.data;
        else if (err?.data?.message) msg = err.data.message;
        else if (err?.data?.detail) msg = err.data.detail;
        toast.error(msg);
      });
  };

  const submitPhoneEntry = () => {
    console.log('lll')
    const fullName = String(draftFullName || "").trim();
    const digits = draftPhone.replace(/\D/g, "").slice(0, 10);
    const next: ContactEntryFieldErrors = {};
    if (!fullName && !isExistingUser) next.name = "Enter your full name.";
    if (!isValidContactPhoneDigits(digits)) {
      next.phone = "Enter a valid 10-digit mobile number .";
    }
    if (Object.keys(next).length) {
      setContactFieldErrors(next);
      return;
    }
    setContactFieldErrors({});
    closeContactEntry();
    const dialCode = countryDialCodeMap[selectedCountry] || "+91";
    const e164 = `${dialCode}${digits}`;
    setPhoneHasOtpError(false);
    setPhoneOtp(["", "", "", "", "", ""]);
    setPhoneCountdown(0);
    sendPhoneOtp({ phoneNumber: e164 })
      .unwrap()
      .then((resp: any) => {
        setShowPhoneOtpModal(true);
        setPhoneCountdown(getOtpCountdownSeconds(resp, 30));
        toast.info(resp?.message || "OTP sent to your mobile number");
        setIsExistingUser(true)
      })
      .catch((err: any) => {
        if (isForbidden(err)) return;

        console.log(err)

        if (err?.status === 500 && err?.data == "Failed to send OTP via SMS: Authenticate") {
         
          return;
        }
        if (err?.status === 500) {
          setShowPhoneOtpModal(true);
          setPhoneCountdown(getOtpCountdownSeconds(err?.data ?? err, 30));
          setPhoneHasOtpError(false);
          return;
        }


        let msg = "Failed to send OTP.";
        if (typeof err?.data === "string") msg = err.data;
        else if (err?.data?.message) msg = err.data.message;
        else if (err?.data?.detail) msg = err.data.detail;
        const normalized = String(msg || "").toLowerCase();





        if (normalized.includes("otp already sent") || normalized.includes("otp already verified")) {
          setShowPhoneOtpModal(true);
          setPhoneCountdown(getOtpCountdownSeconds(err?.data ?? err, 30));
          setPhoneHasOtpError(false);
          return;
        }
        toast.error(msg);
      });
  };

  useEffect(() => {

    let timer: NodeJS.Timeout;
    if (showPhoneOtpModal && phoneCountdown > 0) {
      timer = setInterval(() => setPhoneCountdown((p) => p - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [phoneCountdown, showPhoneOtpModal]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showEmailOtpModal && emailOtpCountdown > 0) {
      timer = setInterval(() => setEmailOtpCountdown((p) => p - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [emailOtpCountdown, showEmailOtpModal]);

  // const handleChangePhoneOtp = (value: string, index: number) => {
  //   if (isNaN(Number(value))) return;
  //   setPhoneHasOtpError(false);
  //   const next = [...phoneOtp];
  //   next[index] = value.substring(value.length - 1);
  //   setPhoneOtp(next);
  //   if (value && index < 5) phoneOtpRefs.current[index + 1]?.focus();
  // };

  const handleChangePhoneOtp = (value: string, index: number) => {
    if (isNaN(Number(value))) return;

    setPhoneHasOtpError(false);

    const newOtp = [...phoneOtp];
    newOtp[index] = value.slice(-1);

    setPhoneOtp(newOtp);

    if (value && index < 5) {
      phoneOtpRefs.current[index + 1]?.focus();
    }

    const otpValue = newOtp.join("");

    if (otpValue.length === 6 && !newOtp.includes("")) {

      handleVerifyPhoneOtpLogin(otpValue); // 👈 pass directly

    }
  };





  const handleKeyDownPhoneOtp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !phoneOtp[index] && index > 0) {
      phoneOtpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyPhoneOtpLogin = async (otp: any) => {

    const digits = draftPhone.replace(/\D/g, "").slice(0, 10);
    const dialCode = countryDialCodeMap[selectedCountry] || "+91";
    const e164 = `${dialCode}${digits}`;
    const name = String(draftFullName || "").trim();

    try {
      const response: any = await verifyPhoneOtp({ phoneNumber: e164, otp: otp, name }).unwrap();
      const payloadData = response?.data ?? response;
      const tokenString = payloadData?.token?.accessToken || payloadData?.token;

      if (response?.data?.coinSummary?.coinsEarned) {
        triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 0, response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 0)

      }
      if (tokenString) {
        persistSessionFromPayload(payloadData, localStorage);
        toast.success(response?.message || "Login successful");
        setShowPhoneOtpModal(false);
        navigate("/dashboard");
        return;
      }
      toast.success(response?.message || "OTP verified");
      setShowPhoneOtpModal(false);
    } catch (error: any) {
      if (error?.originalStatus === 500 || error?.status === 500) {
        setPhoneHasOtpError(true);
        return
      }
      if (error?.originalStatus === 200 || error?.status === 200 || error?.status === 202) {
        const payloadData = error?.data?.data ?? error?.data ?? {};
        const tokenString = payloadData?.token?.accessToken || payloadData?.token;
        if (tokenString) {
          persistSessionFromPayload(payloadData, localStorage);
          toast.success(error?.data?.message || "Login successful");
          setShowPhoneOtpModal(false);
          navigate("/dashboard");
          return;
        }
        toast.success(error?.data?.message || "OTP verified");
        setShowPhoneOtpModal(false);
        return;
      }
      if (isForbidden(error)) return;
      setPhoneHasOtpError(true);
      let msg = "Invalid OTP. Please try again.";
      if (typeof error?.data === "string") msg = error.data;
      else if (error?.data?.message) msg = error.data.message;
      else if (error?.data?.detail) msg = error.data.detail;
      toast.error(msg);
    }
  };

  const handleResendPhoneOtp = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (phoneCountdown !== 0) return;
    const digits = draftPhone.replace(/\D/g, "").slice(0, 10);
    const dialCode = countryDialCodeMap[selectedCountry] || "+91";
    const e164 = `${dialCode}${digits}`;
    try {
      const resp: any = await sendPhoneOtp({ phoneNumber: e164 }).unwrap();
      setPhoneCountdown(getOtpCountdownSeconds(resp, 30));
      toast.info(resp?.message || "OTP has been resent!");
    } catch (error: any) {
      if (isForbidden(error)) return;
      let msg = "Failed to resend OTP.";
      if (typeof error?.data === "string") msg = error.data;
      else if (error?.data?.message) msg = error.data.message;
      else if (error?.data?.detail) msg = error.data.detail;
      toast.error(msg);
    }
  };

  const handleChangeEmailOtp = (value: string, index: number) => {
    if (isNaN(Number(value))) return;
    setEmailHasOtpError(false);
    const next = [...emailOtpDigits];
    next[index] = value.slice(-1);
    setEmailOtpDigits(next);
    if (value && index < 5) {
      emailOtpRefs.current[index + 1]?.focus();
    }
    const otpValue = next.join("");
    if (otpValue.length === 6 && !next.includes("")) {
      void handleVerifyEmailOtp(otpValue);
    }
  };

  const handleKeyDownEmailOtp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !emailOtpDigits[index] && index > 0) {
      emailOtpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyEmailOtp = async (otpArg?: string) => {
    const fullName = String(draftFullName || "").trim();
    const otpValue = otpArg ?? emailOtpDigits.join("");
    if (otpValue.length < 6) {
      setEmailHasOtpError(true);
      toast.error("Please enter the complete OTP");
      return;
    }
    if (!emailToVerify) {
      toast.error("Please enter your email again.");
      setShowEmailOtpModal(false);
      return;
    }
    console.log('asdad')
    try {
      const response: any = await verifyEmailOtpMutation({
        email: emailToVerify,
        otp: otpValue,
        username: fullName,
        // purpose: "REGISTRATION",
      }).unwrap();
      toast.success(response?.message || "OTP verified successfully");
      setShowEmailOtpModal(false);
      console.log('asdadaaaaaaaaa')
      if (response?.data?.coinSummary?.coinsEarned) {
        triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 1, response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 5)

      }

      const payloadData = response?.data ?? response;
      const tokenString = payloadData?.token?.accessToken || payloadData?.token;
      if (tokenString) {
        const patientProfile = payloadData?.patientProfile || {};
        const safeRoles = patientProfile?.role || payloadData?.roles || "USER";
        const familyMembers = payloadData?.familyMembers || [];
        localStorage.setItem("token", tokenString);
        localStorage.setItem("userData", JSON.stringify(patientProfile));
        localStorage.setItem("familyMembers", JSON.stringify(familyMembers));
        const rolesArray = typeof safeRoles === "string" && safeRoles.includes(",") ? safeRoles.split(",") : [safeRoles];
        localStorage.setItem("roles", JSON.stringify(rolesArray));
        dispatch(loginSuccess(tokenString));
        navigate("/dashboard");

      }

    } catch (error: any) {
      console.log(error)
      if (isForbidden(error)) return;
      if (error.status == 500) {
        setEmailHasOtpError(true);
        return
      }

      setEmailHasOtpError(true);
      let msg = "Invalid OTP. Please try again.";
      if (typeof error?.data === "string") msg = error.data;
      else if (error?.data?.message) msg = error.data.message;
      else if (error?.data?.detail) msg = error.data.detail;
      toast.error(msg);
    }
  };

  const handleResendEmailOtp = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (emailOtpCountdown !== 0 || !emailToVerify) return;
    try {
      const resp: any = await generateEmailOtp({ email: emailToVerify }).unwrap();
      setEmailOtpCountdown(getOtpCountdownSeconds(resp, 30));
      setEmailOtpDigits(["", "", "", "", "", ""]);
      setEmailHasOtpError(false);
      toast.info(resp?.message || "OTP has been resent!");
    } catch (error: any) {
      if (isForbidden(error)) return;
      let msg = "Failed to resend OTP.";
      if (typeof error?.data === "string") msg = error.data;
      else if (error?.data?.message) msg = error.data.message;
      else if (error?.data?.detail) msg = error.data.detail;
      toast.error(msg);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins < 10 ? "0" + mins : mins}:${secs < 10 ? "0" + secs : secs}`;
  };
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);



  const handleChangeForgotOtp = (value: string, index: number) => {
    if (isNaN(Number(value))) return;
    setHasOtpError(false);

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // 1. Move focus to next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // 2. Auto-hit API when all 6 digits are present
    const fullOtp = newOtp.join("");
    if (fullOtp.length === 6) {
      handleVerifyOtp(newOtp);
    }
  };


  const handleKeyDownForgotOtp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      setHasOtpError(false);
      otpInputRefs.current[index - 1]?.focus();
    }
  };



  const handleVerifyOtp = async (currentOtp: string[]) => {
    const otpValue = currentOtp.join("");

    try {
      setForgotLoading(true); // This can trigger a global spinner or disable inputs
      const response: any = await verifyOtp({
        emailOrPhone: contactType == "email" ? forgotEmail : "+91" + forgotEmail,
        otp: otpValue,
        purpose: "FORGOT_PASSWORD",
      }).unwrap();

      toast.success(response?.message || "Verified successfully");
      setIsOtpVerified(true);
      setOtpStep(false);
    } catch (error: any) {
      setHasOtpError(true);
      console.log(error)

      if (error.status == 404) {
        return
      }


      let msg = error?.data?.message || error?.data?.detail || "Invalid OTP";
      const normalizedMsg = String(msg).toLowerCase();


      if (normalizedMsg.includes("verified") && normalizedMsg.includes("password")) {
        setIsOtpVerified(true);
        setOtpStep(false);
      } else {
        toast.error(msg);

      }
    } finally {
      setForgotLoading(false);
    }
  };


  const getOtpCountdownSeconds = (resp: any, fallbackSeconds = 30) => {
    const rawSeconds =
      resp?.expiresInSeconds ??
      resp?.data?.expiresInSeconds ??
      resp?.expires_in_seconds ??
      resp?.data?.expires_in_seconds;

    const rawMinutes =
      resp?.expiresInMinutes ??
      resp?.data?.expiresInMinutes ??
      resp?.expires_in_minutes ??
      resp?.data?.expires_in_minutes;

    const seconds = typeof rawSeconds === "string" ? parseInt(rawSeconds, 10) : rawSeconds;
    if (typeof seconds === "number" && seconds > 0) return Math.min(seconds, fallbackSeconds);

    const minutes = typeof rawMinutes === "string" ? parseFloat(rawMinutes) : rawMinutes;
    if (typeof minutes === "number" && minutes > 0) return Math.min(Math.floor(minutes * 60), fallbackSeconds);

    return fallbackSeconds;
  };

  const persistSessionFromPayload = (
    payloadData: any,
    storage: Storage
  ) => {
    const tokenString =
      payloadData?.token?.accessToken ||
      payloadData?.token ||
      payloadData?.data?.token?.accessToken ||
      payloadData?.data?.token;
    const patientProfile =
      payloadData?.patientProfile ||
      payloadData?.data?.patientProfile ||
      payloadData?.user ||
      payloadData?.data?.user ||
      {};
    if (!tokenString) return;

    const normalizedPatientId =
      patientProfile?.patientId ??
      patientProfile?.patientID ??
      patientProfile?.patient_id ??
      payloadData?.patientId ??
      payloadData?.patientID ??
      payloadData?.patient_id ??
      payloadData?.data?.patientId ??
      payloadData?.data?.patientID ??
      payloadData?.data?.patient_id ??
      payloadData?.patientProfileId ??
      payloadData?.data?.patientProfileId ??
      null;

    const mergedProfile =
      normalizedPatientId != null && normalizedPatientId !== ""
        ? { ...patientProfile, patientId: normalizedPatientId }
        : patientProfile;
    const userId =
      mergedProfile?.userId ??
      mergedProfile?.id ??
      payloadData?.userId ??
      payloadData?.id ??
      null;
    const safeRoles = mergedProfile?.role || payloadData?.roles || "USER";
    const familyMembers = payloadData?.familyMembers || [];
    storage.setItem("token", tokenString);
    storage.setItem("userData", JSON.stringify(mergedProfile));
    storage.setItem("familyMembers", JSON.stringify(familyMembers));
    const rolesArray = safeRoles.includes(",") ? safeRoles.split(",") : [safeRoles];
    storage.setItem("roles", JSON.stringify(rolesArray));
    dispatch(loginSuccess(tokenString));

    if (userId) {
      runLoginGamification(String(userId));
      dispatch(hydrateGamification(String(userId)));
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    setLoading(true);
    try {
      const response: any = await loginWithGoogle({ idToken }).unwrap();
      const payloadData = response?.data ?? response;
      const storage = localStorage;
      if (response?.data?.coinSummary?.coinsEarned) {
        triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 0, response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 0)

      }
      if (response?.success || payloadData?.token || payloadData?.patientProfile) {
        persistSessionFromPayload(payloadData, storage);
        toast.success(response?.message || "Login successful");
        navigate("/dashboard");
        return;
      }
      toast.error(response?.message || "Login Error");
    } catch (error: any) {
      if (error?.originalStatus === 200 || error?.status === 200) {
        const payloadData = error?.data?.data || error?.data || {};
        persistSessionFromPayload(payloadData, localStorage);
        toast.success(error?.data?.message || "Login successful");
        navigate("/dashboard");
        return;
      }
      let errorMsg = "Google sign-in failed";
      if (typeof error?.data === "string") {
        errorMsg = error.data;
      } else if (error?.data?.message) {
        errorMsg = error.data.message;
      } else if (error?.data?.detail) {
        errorMsg = error.data.detail;
      } else if (error?.error === "TypeError: Failed to fetch") {
        errorMsg = "Network Error: Failed to connect to the server.";
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .required("Email or Member ID is required")
      .test("email-or-id", "Invalid email format", function (value) {
        if (!value) return false;


        if (value.includes("@")) {

          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) return false;


          const parts = value.split("@");
          if (parts.length < 2 || !parts[1]) return false;

          const domain = parts[1];

          if ((domain.match(/\.com/g) || []).length > 1) return false;

          const tld = domain.split(".").pop();

          if (!tld || !/^[a-zA-Z]{2,}$/.test(tld)) return false;
        }


        return true;
      }),

    password: Yup.string().required("Password is required"),
  });

  const handleSubmit = async (data: any, reset: any) => {


    setLoading(true);
    try {

      const loginData = {
        username: data.username.trim(),
        password: data.password.trim(),
      };

      const response: any = await login(loginData).unwrap()

      if (response?.success || response) {
        const payloadData = response.data || response;
        const storage = data.remember ? localStorage : sessionStorage;
        persistSessionFromPayload(payloadData, storage);

        toast.success(response.message || "Login successful");
        navigate("/dashboard");
      } else {
        toast.error(
          response?.message
            ? response.message
            : "Login Error"
        );
      }
    } catch (error: any) {
      console.log(error)
      if (error?.originalStatus === 200 || error?.status === 200) {
        const payloadData = error?.data?.data || error?.data || {};
        persistSessionFromPayload(payloadData, localStorage);

        toast.success(error?.data?.message || "Login successful");
        navigate("/dashboard");
      } else {
        let errorMsg = "Login error";
        if (typeof error?.data === "string") {
          errorMsg = error.data;
        } else if (error?.data?.message) {
          errorMsg = error.data.message;
        } else if (error?.data?.detail) {
          errorMsg = error.data.detail;
        } else if (error?.error === "TypeError: Failed to fetch") {
          errorMsg = "Network Error: Failed to connect to the server.";
        }

        toast.error(errorMsg);
      }
    }
    finally {
      setLoading(false);  // stop loading
    }
  };

  return (
    <div className="container-fluid h-p100 auth-container">
      <div className="row align-items-center justify-content-md-center h-p100">
        <div className="col-12">
          <div className="row justify-content-center g-0">
            <div className="col-lg-12 col-md-12 col-12">
              <div className="rounded10 shadow-lg translucent-card p-4">
                <div className="card-country-select mb-3">
                  <div className="country-dropdown position-relative" >


                    <Select
                      value={selectedCountry}
                      onChange={(value) => setSelectedCountry(value)}
                      style={{ width: 120, cursor: "pointer" }}
                      placeholder="Select Country"
                    >
                      {countries.map((c) => (
                        <Option key={c.code} value={c.code} style={{ padding: 0 }}>
                          {/* Set padding to 0 on Option to let your inner div fill the space */}
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            padding: "5px 12px", // Move the padding here
                            width: "100%",
                            height: "100%"
                          }}>
                            <img src={c.flag} alt={c.name} width="20" />
                            <span>{c.name}</span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </div>
                </div>

                <div className="content-top-agile p-20 pb-0 text-center">
                  <h2>Welcome Back</h2>
                  <p className="mb-0">Sign in to continue to myTara.</p>
                </div>
                <div className="d-flex justify-content-center align-items-center gap-3 mt-4 mb-2 auth-oauth-icon-row">
                  <AuthIconTooltip label="Continue With Google">
                    <GoogleIconSignIn
                      text="signin_with"
                      disabled={loading}
                      onCredential={handleGoogleCredential}
                      onError={() => toast.error("Google sign-in was cancelled or failed.")}
                    />
                  </AuthIconTooltip>
                  <AuthIconTooltip label="Continue With Email">
                    <button
                      type="button"
                      className="btn btn-outline-light shadow-sm rounded-circle p-3 d-flex align-items-center justify-content-center icon-login-btn"
                      onClick={openEmailEntry}
                    >
                      <Mail size={24} className="text-primary" />
                    </button>
                  </AuthIconTooltip>
                  <AuthIconTooltip label="Continue With Phone Number">
                    <button
                      type="button"
                      className="btn btn-outline-light shadow-sm rounded-circle p-3 d-flex align-items-center justify-content-center icon-login-btn"
                      onClick={openPhoneEntry}
                    >
                      <Smartphone size={24} className="text-success" />
                    </button>
                  </AuthIconTooltip>
                </div>
                <div className="divider-container my-4">
                  <div className="divider-line"></div>
                  <span className="divider-text">OR</span>
                  <div className="divider-line"></div>
                </div>
                <div className="custom-padding form-input-container">
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ touched, errors, setFieldValue }) => (
                      <Form id="login-form" name="login-form" method="post" autoComplete="on">
                        {/* email */}
                        <div className="form-group">
                          <div className="input-group mb-3">
                            <span className={`input-group-text bg-transparent ${touched.username && errors.username ? "border-danger text-danger" : ""
                              }`}>
                              <i className="ti-user"></i>
                            </span>

                            <Field
                              id="username"
                              autoComplete="username"
                              type="text"
                              name="username"

                              className={`form-control ps-15 bg-transparent ${touched.username && errors.username ? "is-invalid" : ""
                                }`}
                              placeholder="Email or Member ID*"
                              onChange={(e: any) => {
                                let value = e.target.value;


                                value = value.replace(/\s/g, "");

                                setFieldValue("username", value);
                              }}
                            />
                          </div>

                          <ErrorMessage
                            name="username"
                            component="div"
                            className="text-danger"
                          />
                        </div>


                        {/* Password */}
                        <div className="form-group">
                          <div className="input-group mb-3">


                            <span
                              className={`input-group-text bg-transparent ${touched.password && errors.password ? "border-danger text-danger" : ""
                                }`}
                            >
                              <i className="ti-lock"></i>
                            </span>


                            <Field
                              id="password"
                              type={showPassword ? "text" : "password"}
                              name="password"
                              className={`form-control bg-transparent ${touched.password && errors.password ? "is-invalid" : ""
                                }`}
                              placeholder="Password*"
                              autoComplete="current-password"
                              onChange={(e: any) => {
                                let value = e.target.value;


                                value = value.replace(/\s/g, "");

                                setFieldValue("password", value);
                              }}
                            />

                            <span
                              tabIndex={-1}
                              className={`input-group-text bg-transparent ${touched.password && errors.password ? "border-danger text-danger" : ""
                                }`}
                              style={{ cursor: "pointer", userSelect: "none" }}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.preventDefault();
                                setShowPassword((prev) => !prev);
                              }}
                            >
                              <i className={`fa ${showPassword ? "fa-eye" : "fa-eye-slash"}`}></i>
                            </span>

                          </div>
                          <ErrorMessage
                            name="password"
                            component="div"
                            className="text-danger"
                          />
                        </div>

                        {/* Remember */}
                        <div className="row">
                          <div className="col-6">
                            <div className="checkbox">
                              <Field
                                type="checkbox"
                                name="remember"
                                id="basic_checkbox_1"
                              />
                              <label htmlFor="basic_checkbox_1">
                                Remember Me
                              </label>
                            </div>
                          </div>

                          <div className="col-6 text-end">
                            <span
                              className="forgot-password-link hover-warning"
                              onClick={() => {
                                setShowForgotModal(true);
                                setForgotEmail("");
                                setOtp(["", "", "", "", "", ""]);
                                setHasOtpError(false);
                                setCountdown(0);
                                setOtpStep(false);
                                setIsOtpVerified(false);
                                setForgotPassword("");
                                setForgotConfirmPassword("");
                              }}
                            >
                              Reset Password?
                            </span>
                          </div>

                          <div className="col-12 text-center mt-3">
                            <button type="submit" className="btn btn-primary px-40" disabled={loading}>
                              {loading ? "LOGIN..." : "SIGN IN"}
                            </button>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>

                  <div className="text-center mt-3">
                    <p className="mb-1" style={{ fontSize: "14px", color: "#6c757d" }}>
                      Don&apos;t have an account?{" "}
                      <Link to="/register" className="text-primary fw-semibold text-decoration-none">
                        Create account
                      </Link>
                    </p>
                  </div>

                  <div className="text-center mt-3">
                    <p style={{ fontSize: "13px", color: "#6c757d", marginTop: "20px" }}>
                      <LockOutlined style={{ marginRight: "6px", color: "#ff7733" }} />
                      Your data is secure & encrypted
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {showForgotModal && (
          <div
            className="modal-backdrop-custom"
            onClick={(e) => {
              // close when clicking outside the dialog
              if (e.target === e.currentTarget) {
                setShowForgotModal(false);
                setForgotEmail("");
                setOtp(["", "", "", "", "", ""]);
                setHasOtpError(false);
                setCountdown(0);
                setOtpStep(false);
                setIsOtpVerified(false);
                setForgotPassword("");
                setForgotConfirmPassword("");
                setForgotEmailError("")
                setShowNewPassword(false);
                setShowConfirmPassword(false);
              }
            }}
          >
            <div className="modal-dialog-custom" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content-custom">
                <div className="modal-header-custom">
                  <h5 className="modal-title-custom">
                    {isOtpVerified ? "Reset Password" : otpStep ? "Enter Security Code" : "Reset Password"}
                  </h5>
                  <button
                    type="button"
                    className="register-contact-clear1"
                    aria-label="Clear"
                    onClick={() => {
                      setShowForgotModal(false);
                      setForgotEmail("");
                      setOtp(["", "", "", "", "", ""]);
                      setHasOtpError(false);
                      setCountdown(0);
                      setOtpStep(false);
                      setIsOtpVerified(false);
                      setForgotPassword("");
                      setForgotConfirmPassword("");
                      setForgotEmailError("")
                      setShowNewPassword(false);
                      setShowConfirmPassword(false);
                    }}
                  >
                    ×
                  </button>


                </div>
                <div className="modal-body-custom">
                  {!otpStep && !isOtpVerified && (
                    <div className="forgot-password-container" style={{ animation: 'fadeIn 0.3s ease-in' }}>

                      {/* 1. Header Label */}
                      <label className="form-label mb-3" style={{ fontWeight: 600, color: '#495057' }}>
                        Recovery Method
                      </label>

                      {/* 2. Custom Selector Cards (Replaces standard Radios) */}
                      <div className="d-flex gap-3 mb-4">
                        {['email', 'phone'].map((type: any) => (
                          <div
                            key={type}
                            onClick={() => {
                              setContactType(type);
                              setForgotEmail("");
                              setForgotEmailError("");
                            }}
                            style={{
                              flex: 1,
                              padding: '12px',
                              border: `2px solid ${contactType === type ? '#5156be' : '#e9ecef'}`,
                              borderRadius: '10px',
                              cursor: 'pointer',
                              textAlign: 'center',
                              backgroundColor: contactType === type ? '#f8f9ff' : '#fff',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px'
                            }}
                          >
                            {/* Custom Radio Circle */}
                            <div style={{
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              border: `2px solid ${contactType === type ? '#5156be' : '#adb5bd'}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {contactType === type && (
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#5156be' }} />
                              )}
                            </div>
                            <span style={{
                              fontSize: '14px',
                              fontWeight: contactType === type ? 600 : 500,
                              color: contactType === type ? '#5156be' : '#6c757d',
                              textTransform: 'capitalize'
                            }}>
                              {type}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* 3. Input Field Section */}
                      <div className="form-group mb-2">
                        <label className="form-label" style={{ fontSize: '13px', color: '#6c757d' }}>
                          {contactType === "phone" ? "Registered Mobile Number" : "Registered Email Address"}
                        </label>

                        {/* 1. Relative Container */}
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <input
                            type="text"
                            className={`form-control `}
                            style={{
                              padding: '12px',
                              paddingRight: '40px', // 2. Add space on the right for the icon
                              borderRadius: '8px',
                              border: forgotEmailError ? '1px solid #dc3545' : '1px solid #dee2e6',
                              fontSize: '15px',
                              transition: 'border-color 0.2s',
                              width: '100%'
                            }}
                            value={forgotEmail}
                            onChange={(e) => {
                              let value = e.target.value;
                              if (contactType === "phone") {
                                value = value.replace(/\D/g, "").slice(0, 10);
                                setForgotEmail(value);
                                if (value && !/^[6-9]\d{9}$/.test(value)) {
                                  setForgotEmailError("Enter a valid 10-digit phone number.");
                                } else {
                                  setForgotEmailError("");
                                }
                              } else {
                                setForgotEmail(value);
                                if (value && !isValidContactEmail(value)) {
                                  setForgotEmailError("Enter a valid email address.");
                                } else {
                                  setForgotEmailError("");
                                }
                              }
                            }}
                            placeholder={contactType === "phone" ? "98765 43210" : "name@example.com"}
                          />

                          {/* 3. Cross Icon (Only visible if there is a value) */}
                          {forgotEmail && (
                            <span
                              onClick={() => {
                                setForgotEmail("");
                                setForgotEmailError("");
                              }}
                              style={{
                                position: 'absolute',
                                right: '12px',
                                cursor: 'pointer',
                                color: '#adb5bd',
                                fontSize: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'color 0.2s',
                                userSelect: 'none'
                              }}
                              className="register-contact-clear1"
                              onMouseOver={(e) => (e.currentTarget.style.color = '#5156be')} // 4. Indigo hover effect
                              onMouseOut={(e) => (e.currentTarget.style.color = '#adb5bd')}
                            >
                              &times;
                            </span>
                          )}
                        </div>
                      </div>

                      {/* 4. Error Message Display */}
                      {forgotEmailError && (
                        <div className="text-danger mb-3" style={{ fontSize: "12px", display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span className="badge bg-danger-soft">!</span> {forgotEmailError}
                        </div>
                      )}

                      {/* 5. Submit Button */}
                      <button
                        type="button"
                        className="btn w-100 py-2 mt-3"
                        disabled={forgotLoading || !forgotEmail || !!forgotEmailError}
                        style={{
                          backgroundColor: '#5156be',
                          color: '#fff',
                          borderRadius: '8px',
                          fontWeight: 600,
                          letterSpacing: '0.5px',
                          boxShadow: '0 4px 6px rgba(81, 86, 190, 0.2)',
                          opacity: (forgotLoading || !forgotEmail || !!forgotEmailError) ? 0.7 : 1,
                          transition: 'all 0.3s'
                        }}
                        onClick={async () => {
                          if (contactType === "email") {
                            if (!isValidContactEmail(forgotEmail)) {
                              setForgotEmailError("Enter a valid email address.");
                              return;
                            }
                          } else {
                            const digitsOnly = forgotEmail.replace(/\D/g, "");
                            if (digitsOnly.length < 10) {
                              setForgotEmailError("Enter a valid phone number.");
                              return;
                            }
                          }

                          setForgotEmailError("");

                          try {
                            setForgotLoading(true);

                            const response: any = await sendOtp({
                              emailOrPhone: contactType === "email" ? forgotEmail : "+91" + forgotEmail,
                              purpose: "FORGOT_PASSWORD"
                            }).unwrap();

                            setCountdown(getOtpCountdownSeconds(response, 30));
                            setOtpStep(true);
                            setOtp(["", "", "", "", "", ""]);
                            setHasOtpError(false);

                            toast.info(
                              response?.message ||
                              (contactType === "phone"
                                ? "OTP sent to your phone"
                                : "OTP sent to your email")
                            );

                          } catch (error: any) {


                            if (error?.status == 400) {

                              return
                            }

                            if (error?.status == 409) {
                              toast.error(error?.data)
                              setIsOtpVerified(true);
                              setOtpStep(false);
                              return
                            }
                            let msg = error?.data?.message || error?.data || "Failed to send OTP";

                            const lowerMsg = msg.toLowerCase();

                            if (lowerMsg.includes("user does not exist")) {
                              toast.error("No account found. Please register first.");
                            } else if (lowerMsg.includes("otp already sent")) {
                              setCountdown(30);
                              setOtpStep(true);
                            } else {
                              toast.error(msg);
                            }
                          } finally {
                            setForgotLoading(false);
                          }
                        }}
                      >
                        {forgotLoading ? (
                          <div className="spinner-border spinner-border-sm me-2" role="status" />
                        ) : "SEND OTP"}
                      </button>

                      {/* Optional: Navigation Link */}

                    </div>
                  )}



                  {otpStep && !isOtpVerified && (
                    <>
                      <p className="text-muted fs-14 mb-2">
                        Code sent to <strong>{forgotEmail}</strong>
                      </p>
                      <div className="otp-container d-flex justify-content-between mb-3" style={{ gap: "10px" }}>
                        {otp.map((digit, index) => (
                          <input
                            key={index}
                            type="text"
                            maxLength={1}
                            value={digit}
                            ref={(el) => {
                              otpInputRefs.current[index] = el;
                            }}
                            onChange={(e) => handleChangeForgotOtp(e.target.value, index)}
                            onKeyDown={(e) => handleKeyDownForgotOtp(e, index)}
                            className={`form-control otp-input ${digit ? "filled" : ""} ${hasOtpError ? "border-danger" : ""}`}
                            style={{
                              width: "45px",
                              height: "55px",
                              textAlign: "center",
                              fontSize: "24px",
                              borderRadius: "10px",
                              borderColor: hasOtpError ? "#dc3545" : undefined,
                            }}
                          />
                        ))}
                      </div>
                      <div className="auth-otp-resend mb-3">
                        <p className="auth-otp-resend__hint">Didn&apos;t receive the code?</p>
                        {countdown > 0 ? (
                          <div className="auth-otp-resend__timer" role="status" aria-live="polite" aria-atomic="true">
                            <span className="auth-otp-resend__timer-label">Resend in</span>
                            <span className="auth-otp-resend__timer-value">{countdown}s</span>
                          </div>
                        ) : (
                          <a
                            href="#"
                            className="auth-otp-resend__link"
                            onClick={async (e) => {
                              e.preventDefault();
                              if (!forgotEmail) {
                                toast.error("Please enter your email first");
                                return;
                              }
                              try {
                                setForgotLoading(true);
                                const response: any = await generateEmailOtp({ email: forgotEmail }).unwrap();
                                setCountdown(getOtpCountdownSeconds(response, 30));
                                setOtp(["", "", "", "", "", ""]);
                                setHasOtpError(false);
                                toast.info(response?.message || "OTP resent");
                              } catch (error: any) {
                                let msg = "Failed to resend OTP";
                                if (typeof error?.data === "string") {
                                  msg = error.data;
                                } else if (error?.data?.message) {
                                  msg = error.data.message;
                                } else if (error?.data?.detail) {
                                  msg = error.data.detail;
                                }

                                // Same special case when resending: keep user in popup, move to OTP step
                                if (
                                  typeof msg === "string" &&
                                  msg.toLowerCase().includes("email already verified") &&
                                  msg.toLowerCase().includes("please proceed with signup")
                                ) {
                                  setCountdown(30);
                                  setOtpStep(true);
                                  setOtp(["", "", "", "", "", ""]);
                                  setHasOtpError(false);
                                } else if (
                                  typeof msg === "string" &&
                                  msg.toLowerCase().includes("otp already verified") &&
                                  (msg.toLowerCase().includes("reset your password") ||
                                    msg.toLowerCase().includes("reset password") ||
                                    msg.toLowerCase().includes("proceed to reset"))
                                ) {
                                  // Keep popup and show OTP entry step.
                                  setCountdown(30);
                                  setOtpStep(true);
                                  setOtp(["", "", "", "", "", ""]);
                                  setHasOtpError(false);
                                } else if (
                                  typeof msg === "string" &&
                                  msg.toLowerCase().includes("otp already sent")
                                ) {
                                  // Avoid duplicate toasts: global apiSlice already shows the message.
                                  // Keep user in OTP step.
                                  setCountdown(30);
                                  setOtpStep(true);
                                  setHasOtpError(false);
                                } else {
                                  toast.error(msg);
                                }
                              } finally {
                                setForgotLoading(false);
                              }
                            }}
                          >
                            Resend code
                          </a>
                        )}
                      </div>

                    </>
                  )}

                  {isOtpVerified && (
                    <>
                      {/* New Password */}
                      <label className="form-label">New Password</label>
                      <div style={{ position: "relative" }} className="mb-1">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          className="form-control"
                          style={{ paddingRight: "40px" }}
                          value={forgotPassword}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForgotPassword(value);
                            if (!value) {
                              setForgotPasswordError("New password is required");
                            } else if (value.length < 8) {
                              setForgotPasswordError("Password must be at least 8 characters");
                            } else if (!/[A-Z]/.test(value)) {
                              setForgotPasswordError("Must contain at least one uppercase letter");
                            } else if (!/[a-z]/.test(value)) {
                              setForgotPasswordError("Must contain at least one lowercase letter");
                            } else if (!/[0-9]/.test(value)) {
                              setForgotPasswordError("Must contain at least one number");
                            } else if (!/[!@#$%^&*(),.?\":{}|<>]/.test(value)) {
                              setForgotPasswordError("Must contain at least one special character");
                            } else {
                              setForgotPasswordError("");
                            }
                          }}
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(p => !p)}
                          style={{
                            position: "absolute", right: "10px", top: "50%",
                            transform: "translateY(-50%)", background: "none",
                            border: "none", cursor: "pointer", padding: 0,
                            fontSize: "16px",
                          }}
                          className="ey-icon"
                        >
                          <i className={` ey-icon fa ${!showNewPassword ? "fa-eye-slash" : "fa-eye"}`} />
                        </button>
                      </div>
                      {forgotPasswordError && (
                        <div className="text-danger mb-1" style={{ fontSize: "12px" }}>
                          {forgotPasswordError}
                        </div>
                      )}

                      {/* Confirm Password */}
                      <label className="form-label">Confirm Password</label>
                      <div style={{ position: "relative" }} className="mb-1">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className="form-control"
                          style={{ paddingRight: "40px" }}
                          value={forgotConfirmPassword}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForgotConfirmPassword(value);
                            if (!value) {
                              setForgotConfirmPasswordError("Please confirm your password");
                            } else if (value !== forgotPassword) {
                              setForgotConfirmPasswordError("Passwords do not match");
                            } else {
                              setForgotConfirmPasswordError("");
                            }
                          }}
                          placeholder="Confirm new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(p => !p)}
                          style={{
                            position: "absolute", right: "10px", top: "50%",
                            transform: "translateY(-50%)", background: "none",
                            border: "none", cursor: "pointer", padding: 0,
                            fontSize: "16px",
                          }}
                          className="ey-icon"
                        >
                          <i className={` ey-icon fa ${!showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`} />
                        </button>
                      </div>
                      {forgotConfirmPasswordError && (
                        <div className="text-danger mb-2" style={{ fontSize: "12px" }}>
                          {forgotConfirmPasswordError}
                        </div>
                      )}

                      <button
                        type="button"
                        className="btn btn-primary w-100 mt-4"
                        onClick={async () => {
                          if (!forgotPassword) {
                            setForgotPasswordError("New password is required");
                            return;
                          }
                          if (!forgotConfirmPassword) {
                            setForgotConfirmPasswordError("Please confirm your password");
                            return;
                          }
                          if (forgotPasswordError) {
                            toast.error("Please fix password errors");
                            return;
                          }
                          if (forgotConfirmPassword !== forgotPassword) {
                            setForgotConfirmPasswordError("Passwords do not match");

                            return;
                          }
                          try {
                            setForgotLoading(true);
                            const response: any = await forgotPasswordApi({
                              emailOrPhone: forgotEmail,
                              newPassword: forgotPassword,
                              confirmPassword: forgotConfirmPassword,
                            }).unwrap();
                            toast.success(response?.message || "Password reset successfully");
                            setShowForgotModal(false);
                            setForgotEmail("");
                            setOtp(["", "", "", "", "", ""]);
                            setHasOtpError(false);
                            setCountdown(0);
                            setOtpStep(false);
                            setIsOtpVerified(false);
                            setForgotPassword("");
                            setForgotConfirmPassword("");
                            setForgotPasswordError("");
                            setForgotConfirmPasswordError("");
                            setShowNewPassword(false);
                            setShowConfirmPassword(false);
                          } catch (error: any) {
                            let msg = "Failed to reset password";
                            if (typeof error?.data === "string") msg = error.data;
                            else if (error?.data?.message) msg = error.data.message;
                            else if (error?.data?.detail) msg = error.data.detail;
                            toast.error(msg);
                          } finally {
                            setForgotLoading(false);
                          }
                        }}
                      >
                        {forgotLoading ? "Saving..." : "Save Password"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <ContactEntryDialogs
          kind={contactEntryKind}
          onClose={closeContactEntry}
          phoneCollectFullName={true}
          draftFullName={draftFullName}
          onDraftFullNameChange={setDraftFullName}
          draftEmail={draftEmail}
          onDraftEmailChange={setDraftEmail}
          draftPhone={draftPhone}
          onDraftPhoneChange={setDraftPhone}
          fieldErrors={contactFieldErrors}
          onClearContactFieldError={clearContactFieldError}
          onEmailNext={submitEmailEntry}
          onPhoneNext={submitPhoneEntry}
          checkUser={setIsExistingUser}
          isExistingUser={isExistingUser}
        />

        <Dialog
          open={showEmailOtpModal}
          onClose={() => setShowEmailOtpModal(false)}
          maxWidth="xs"
          fullWidth
          className="auth-modal glass-modal"
          BackdropProps={{
            style: { backgroundColor: "rgba(0, 0, 0, 0.2)", backdropFilter: "blur(5px)" },
          }}
        >
          <IconButton
            className="auth-modal-round-close"
            aria-label="close"
            onClick={() => setShowEmailOtpModal(false)}
          >
            <CloseIcon />
          </IconButton>
          <DialogTitle className="text-center pt-30 pb-10">
            <h2>Enter Security Code</h2>
            <p className="text-muted fs-14 mb-0">Code sent to {emailToVerify}</p>
          </DialogTitle>
          <DialogContent className="px-40 pb-30">
            <div className="mt-3 text-center form-input-container">
              <div className="otp-container d-flex justify-content-between mb-30" style={{ gap: "10px" }}>
                {emailOtpDigits.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength={1}
                    value={digit}
                    ref={(el) => {
                      emailOtpRefs.current[index] = el;
                    }}
                    onChange={(e) => handleChangeEmailOtp(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDownEmailOtp(e, index)}
                    className={`form-control otp-input ${digit ? "filled" : ""} ${emailHasOtpError ? "border-danger" : ""}`}
                  />
                ))}
              </div>

              <div className="auth-otp-resend mt-30">
                <p className="auth-otp-resend__hint">Didn&apos;t receive the code?</p>
                {emailOtpCountdown > 0 ? (
                  <div className="auth-otp-resend__timer" role="status" aria-live="polite" aria-atomic="true">
                    <span className="auth-otp-resend__timer-label">Resend in</span>
                    <span className="auth-otp-resend__timer-value">{formatTime(emailOtpCountdown)}</span>
                  </div>
                ) : (
                  <a href="#" onClick={handleResendEmailOtp} className="auth-otp-resend__link">
                    Resend code
                  </a>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={showPhoneOtpModal}
          onClose={() => setShowPhoneOtpModal(false)}
          maxWidth="xs"
          fullWidth
          className="auth-modal glass-modal"
          BackdropProps={{
            style: { backgroundColor: "rgba(0, 0, 0, 0.2)", backdropFilter: "blur(5px)" },
          }}
        >
          <IconButton
            className="auth-modal-round-close"
            aria-label="close"
            onClick={() => setShowPhoneOtpModal(false)}
          >
            <CloseIcon />
          </IconButton>
          <DialogTitle className="text-center pt-30 pb-10">
            <h2>Enter Security Code</h2>
            <p className="text-muted fs-14 mb-0">
              Code sent to {draftPhone.replace(/\D/g, "").slice(0, 10)}
            </p>
          </DialogTitle>
          <DialogContent className="px-40 pb-30">
            <div className="mt-3 text-center form-input-container">
              <div className="otp-container d-flex justify-content-between mb-30" style={{ gap: "10px" }}>
                {phoneOtp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength={1}
                    value={digit}
                    ref={(el) => {
                      phoneOtpRefs.current[index] = el;
                    }}
                    onChange={(e) => handleChangePhoneOtp(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDownPhoneOtp(e, index)}
                    className={`form-control otp-input ${digit ? "filled" : ""} ${phoneHasOtpError ? "border-danger" : ""}`}
                  />
                ))}
              </div>
              {/* 
              <button type="button" className="btn btn-primary px-40" onClick={handleVerifyPhoneOtpLogin}>
                VERIFY CODE
              </button> */}

              <div className="auth-otp-resend mt-30">
                <p className="auth-otp-resend__hint">Didn&apos;t receive the code?</p>
                {phoneCountdown > 0 ? (
                  <div className="auth-otp-resend__timer" role="status" aria-live="polite" aria-atomic="true">
                    <span className="auth-otp-resend__timer-label">Resend in</span>
                    <span className="auth-otp-resend__timer-value">{formatTime(phoneCountdown)}</span>
                  </div>
                ) : (
                  <a href="#" onClick={handleResendPhoneOtp} className="auth-otp-resend__link">
                    Resend code
                  </a>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
